/**
 * Bottom-centre readouts: the active order tool with its instructions, and the chart-table
 * coordinate strip describing the hex under the pointer (set by the renderer via `setHover`).
 */
import { isSpectatorView, NO_FACTION, NO_HEX, TERRAIN_COUNT, Terrain } from '@friction/engine';
import { cssColor, factionOf, hexLabel } from '../../game/format';
import { contactsAt, unitsAt, useGame } from '../../game/store';
import { formatTemp, skyAt, weatherEffects } from '../../game/weather';
import { useT } from '../../i18n';
import { cityName, nationLabel, regionName } from '../../i18n/names';
import { cx, Keycap } from '../common/controls';
import { Icon } from '../common/Icon';
import hud from './hud.module.css';

function ToolBanner() {
  const t = useT();
  const tool = useGame((g) => g.tool);
  const count = useGame((g) => g.selection.length);
  const hHour = useGame((g) => g.draft.hHour);
  if (tool === 'select' || !count) return null;
  return (
    <div className={cx(hud.tool, tool === 'attack' && hud.toolAttack)}>
      <Icon name={tool} size={16} />
      <span className={hud.toolName}>{t(`tool.${tool}`)}</span>
      <span>
        {t(`tool.${tool}.hint`)} · {t('tool.formations', { n: count })}
        {hHour !== null && t('tool.withHHour')}
      </span>
      <span className={hud.toolHint}>
        <Keycap>{t('key.shift')}</Keycap> {t('tool.keep')} <Keycap>Esc</Keycap> {t('tool.cancel')}
      </span>
    </div>
  );
}

function HexInfo() {
  const t = useT();
  const hex = useGame((g) => g.hoverHex);
  const view = useGame((g) => g.view);
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  if (hex === NO_HEX || !view || !map || !grid || hex >= grid.count) return null;
  const terrain = map.terrain[hex];
  const cityId = map.cityAt[hex];
  const city = cityId >= 0 ? map.cities[cityId] : null;
  const owner = view.owner[hex];
  const f = owner !== NO_FACTION ? factionOf(view, owner) : undefined;
  const region = map.region[hex] >= 0 ? map.regions[map.region[hex]]?.name : null;
  // The omniscient replay view has no "own" side: every unit is simply there.
  const omniscient = isSpectatorView(view);
  const own = unitsAt(view, hex, omniscient ? undefined : view.faction).length;
  const enemy = contactsAt(view, hex).length;
  const supply =
    owner === view.faction || (omniscient && owner !== NO_FACTION) ? view.supply[hex] / 255 : null;
  const sky = terrain >= Terrain.Plains ? skyAt(view, map, grid, hex, t.locale) : null;
  return (
    <div className={hud.hex}>
      <span className={hud.hexCoord}>{hexLabel(grid, hex)}</span>
      <span className={hud.hexTerrain}>
        {terrain >= 0 && terrain < TERRAIN_COUNT ? t(`terrain.${terrain as Terrain}`) : t('terrain.unknown')}
      </span>
      {sky && (
        <>
          <span className={hud.sep}>·</span>
          <span className={hud.hexWx}>
            <Icon name={sky.icon} size={14} />
            {sky.word} {formatTemp(sky.temp, t.locale)}
            {sky.ground && <span className={hud.hexGround}> {sky.ground}</span>}
          </span>
        </>
      )}
      {city && (
        <>
          <span className={hud.sep}>·</span>
          <span className={hud.hexCity}>
            {cityName(city.name, t.locale)}{' '}
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{t('hex.vp', { vp: city.vp })}</span>
          </span>
        </>
      )}
      {region && !city && (
        <>
          <span className={hud.sep}>·</span>
          <span style={{ fontStyle: 'italic', fontFamily: 'var(--font-serif)', fontSize: 13 }}>
            {regionName(region, t.locale)}
          </span>
        </>
      )}
      {f && (
        <>
          <span className={hud.sep}>·</span>
          <span className={hud.dot} style={{ background: cssColor(f.color) }} />
          <span>
            {nationLabel(f, t.locale)}
            {!view.visible[hex] && t('hex.lastKnown')}
          </span>
        </>
      )}
      {supply !== null && (
        <>
          <span className={hud.sep}>·</span>
          <span>{t('hex.supply', { pct: t.pct(supply).replace(/\s/g, '') })}</span>
        </>
      )}
      {(own > 0 || enemy > 0) && (
        <>
          <span className={hud.sep}>·</span>
          <span>
            {own > 0 && (omniscient ? t('hex.divisions', { n: own }) : t('hex.own', { n: own }))}
            {own > 0 && enemy > 0 && ', '}
            {enemy > 0 && <span style={{ color: '#f08a76' }}>{t('hex.enemy', { n: enemy })}</span>}
          </span>
        </>
      )}
      {!view.visible[hex] && terrain >= Terrain.Plains && (
        <>
          <span className={hud.sep}>·</span>
          <span style={{ color: 'var(--faint)' }}>{t('hex.unobserved')}</span>
        </>
      )}
    </div>
  );
}

/** What the weather at the hovered hex does to operations — a slip just above the readout. */
function WeatherNote() {
  const t = useT();
  const hex = useGame((g) => g.hoverHex);
  const view = useGame((g) => g.view);
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  if (hex === NO_HEX || !view || !map || !grid || hex >= grid.count) return null;
  const cond = view.weather.conditions[hex] ?? 0;
  const effects = weatherEffects(cond, map.terrain[hex] === Terrain.Marsh, t.locale);
  if (!effects.length) return null;
  const sky = skyAt(view, map, grid, hex, t.locale);
  const head = [
    sky.kind === 'fair' || sky.kind === 'clear' || sky.kind === 'overcast' ? null : sky.word,
    sky.ground,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className={hud.wxNote}>
      <span className={hud.wxNoteHead}>{head.toUpperCase()}</span>
      {effects.join(' · ')}
    </div>
  );
}

export function HexReadout() {
  return (
    <div className={hud.readout} data-tut="readout">
      <ToolBanner />
      <WeatherNote />
      <HexInfo />
    </div>
  );
}
